import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { useAudioPlayer, Stream } from '../contexts/AudioPlayerContext';
import listenHistoryService, { ListenHistoryEntry, ListenStats } from '../services/listenHistoryService';
import { logger } from '../utils/logger';
import './ListenHistory.css';

const ListenHistory: React.FC = () => {
  const { playStream, addToQueue } = useAudioPlayer();
  const [history, setHistory] = useState<ListenHistoryEntry[]>([]);
  const [stats, setStats] = useState<ListenStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadHistory = useCallback(async (token?: string) => {
    try {
      if (token) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const [historyResponse, statsResponse] = await Promise.all([
        listenHistoryService.getHistory(20, token),
        !token ? listenHistoryService.getStats() : Promise.resolve(null)
      ]);

      if (token) {
        setHistory(prev => [...prev, ...historyResponse.history]);
      } else {
        setHistory(historyResponse.history);
        if (statsResponse) {
          setStats(statsResponse);
        }
      }
      setNextToken(historyResponse.nextToken);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load history';
      setError(message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleLoadMore = () => {
    if (nextToken && !loadingMore) {
      loadHistory(nextToken);
    }
  };

  const handlePlay = (e: React.MouseEvent, entry: ListenHistoryEntry) => {
    e.preventDefault();
    e.stopPropagation();
    if (entry.audioUrl) {
      const stream: Stream = {
        streamId: entry.streamId,
        name: entry.streamName,
        description: entry.streamDescription,
        audioUrl: entry.audioUrl,
        coverImage: entry.coverImage,
        uploaderUsername: entry.uploaderUsername,
        uploaderDisplayName: entry.uploaderDisplayName,
      };
      playStream(stream);
    }
  };

  const handleAddToQueue = (e: React.MouseEvent, entry: ListenHistoryEntry) => {
    e.preventDefault();
    e.stopPropagation();
    if (entry.audioUrl) {
      const stream: Stream = {
        streamId: entry.streamId,
        name: entry.streamName,
        description: entry.streamDescription,
        audioUrl: entry.audioUrl,
        coverImage: entry.coverImage,
        uploaderUsername: entry.uploaderUsername,
        uploaderDisplayName: entry.uploaderDisplayName,
      };
      addToQueue(stream);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Are you sure you want to clear your listen history?')) {
      return;
    }

    try {
      setClearing(true);
      await listenHistoryService.clearHistory();
      setHistory([]);
      setStats(null);
    } catch (err) {
      logger.error('Failed to clear history', err);
    } finally {
      setClearing(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="history-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading your history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-page">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => loadHistory()} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <header className="page-header">
        <div className="header-content">
          <div>
            <h1>Listen History</h1>
            <p className="subtitle">Your recently played streams</p>
          </div>
          {history.length > 0 && (
            <button
              onClick={handleClearHistory}
              disabled={clearing}
              className="btn btn-secondary btn-sm"
            >
              {clearing ? 'Clearing...' : 'Clear History'}
            </button>
          )}
        </div>
      </header>

      {stats && history.length > 0 && (
        <div className="stats-bar">
          <div className="stat">
            <span className="stat-value">{stats.totalListens}</span>
            <span className="stat-label">Listens</span>
          </div>
          <div className="stat">
            <span className="stat-value">{stats.uniqueStreams}</span>
            <span className="stat-label">Unique Streams</span>
          </div>
          <div className="stat">
            <span className="stat-value">{stats.completedListens}</span>
            <span className="stat-label">Completed</span>
          </div>
          <div className="stat">
            <span className="stat-value">{formatDuration(stats.totalListenTime)}</span>
            <span className="stat-label">Total Time</span>
          </div>
        </div>
      )}

      {history.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎧</div>
          <h3>No listen history</h3>
          <p>Start listening to streams to build your history.</p>
          <Link to="/streams" className="btn btn-primary">
            Browse Streams
          </Link>
        </div>
      ) : (
        <>
          <div className="history-list">
            {history.map((entry, index) => (
              <div key={`${entry.streamId}-${entry.timestamp}-${index}`} className="history-item">
                <Link
                  to={`/streams/${entry.streamId}`}
                  className="history-link"
                >
                  <div className="history-cover">
                    {entry.coverImage ? (
                      <img src={entry.coverImage} alt={entry.streamName} />
                    ) : (
                      <div className="cover-placeholder">🎵</div>
                    )}
                  </div>
                  <div className="history-info">
                    <h3 className="history-name">{entry.streamName}</h3>
                    {entry.uploaderUsername && (
                      <p className="history-uploader">by @{entry.uploaderUsername}</p>
                    )}
                    <div className="history-meta">
                      <span className="history-time">{entry.timeAgo}</span>
                      {entry.progressPercent !== undefined && (
                        <span className="history-progress">
                          {entry.completed ? 'Completed' : `${entry.progressPercent}%`}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
                <div className="history-actions">
                  <button
                    className="action-button play"
                    onClick={(e) => handlePlay(e, entry)}
                    title="Play"
                  >
                    ▶
                  </button>
                  <button
                    className="action-button"
                    onClick={(e) => handleAddToQueue(e, entry)}
                    title="Add to queue"
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>

          {nextToken && (
            <div className="load-more-container">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="btn btn-secondary"
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ListenHistory;
